/**
 * API Service Layer for Litla Drivers App
 */

import { getCsrfToken, refreshCsrfToken } from './csrf';
import { logger } from '../utils/logger';

// API base URL - uses production domain by default, overrideable via VITE_API_URL env var
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.gamaleigan.is';

// ============================================
// Type Definitions
// ============================================

/** User information returned from authentication */
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'driver' | 'customer' | 'support';
  phone?: string;
  avatar_url?: string;
  last_login?: string;
  created_at: string;
  updated_at: string;
}

/** Route stop information */
export interface RouteStop {
  id: string;
  route_id: string;
  order_id: string;
  container_id: string;
  sequence: number;
  status: 'pending' | 'completed' | 'skipped';
  arrival_time?: string;
  departure_time?: string;
  notes?: string;
  location_address: string;
  location_lat: number;
  location_lon: number;
}

/** Route information */
export interface Route {
  id: string;
  driver_id: string;
  date: string;
  status: 'pending' | 'in_progress' | 'completed';
  stops: RouteStop[];
  total_distance_km?: number;
  estimated_duration_minutes?: number;
  created_at: string;
  updated_at: string;
}

/** Order information */
export interface Order {
  id: string;
  customer_id: string;
  customer_name?: string;
  container_id?: string;
  type: 'rental' | 'pickup' | 'delivery' | 'service';
  status: 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  scheduled_date?: string;
  completed_date?: string;
  notes?: string;
  total_amount?: number;
  invoice_id?: string;
  driver_id?: string;
  created_at: string;
  updated_at: string;
}

/** Container information */
export interface Container {
  id: string;
  type: 'open' | 'closed' | 'material';
  waste_type: string;
  status: 'available' | 'rented' | 'maintenance' | 'transit';
  customer_id?: string;
  location_address?: string;
  location_lat?: number;
  location_lon?: number;
  fill_level?: number;
  temperature?: number;
  humidity?: number;
  door_status?: 'open' | 'closed';
  weight_kg?: number;
  battery_level?: number;
  last_reading?: string;
  rental_start_date?: string;
  rental_end_date?: string;
  ai_monitoring_enabled: boolean;
  created_at: string;
  updated_at: string;
}

/** Photo record */
export interface OrderPhoto {
  id: string;
  url: string;
  filename: string;
  timestamp: string;
}

/**
 * Internal fetch helper with CSRF protection
 * Automatically includes CSRF token for mutating requests (POST, PUT, DELETE, PATCH)
 */
async function fetchApi<T>(endpoint: string, options: RequestInit = {}, retryOnCsrf = true): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const method = options.method?.toUpperCase() || 'GET';

  // Determine if this is a mutating request that needs CSRF token
  const isMutatingRequest = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);

  // Get CSRF token for mutating requests
  const csrfToken = isMutatingRequest ? getCsrfToken() : null;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include', // Important: Send cookies (csrf_token cookie)
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(csrfToken && { 'X-CSRF-Token': csrfToken }), // Add CSRF token for mutating requests
      ...options.headers,
    },
  });

  // Handle 401 Unauthorized - redirect to login
  if (response.status === 401) {
    localStorage.removeItem('auth_token');
    window.location.href = '/login';
    throw new Error('Unauthorized - redirecting to login');
  }

  // Handle 403 CSRF token validation failure
  if (response.status === 403 && retryOnCsrf) {
    const error = await response.json().catch(() => ({ error: 'Forbidden' }));

    // Check if this is a CSRF error
    if (error.error?.toLowerCase().includes('csrf')) {
      logger.warn('[API] CSRF token validation failed, refreshing token and retrying...');

      // Refresh CSRF token
      await refreshCsrfToken();

      // Retry request once with new token (retryOnCsrf = false to prevent infinite loop)
      return fetchApi<T>(endpoint, options, false);
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `API Error: ${response.status}`);
  }

  return response.json();
}

// Auth
export const authApi = {
  async login(email: string, password: string) {
    // Backend returns { success, data: { user, token } }
    const response = await fetchApi<{ success: boolean; data: { user: User; token?: string } }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    const result = response.data;
    if (result?.token) {
      localStorage.setItem('auth_token', result.token);
    }
    if (result?.user) {
      localStorage.setItem('user_data', JSON.stringify(result.user));
    }

    // Fetch CSRF token after successful login
    // Login endpoint sets the csrf_token cookie, now we need to get the token value
    const { fetchCsrfToken } = await import('./csrf');
    fetchCsrfToken().catch((error) => {
      logger.error('[Auth] Failed to fetch CSRF token after login:', error);
    });

    return result;
  },

  async googleLogin(idToken: string) {
    // Call Google auth endpoint
    const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ idToken }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Google login failed' }));
      throw new Error(error.error || 'Google innskráning mistókst');
    }

    const data = await response.json();
    // Backend wraps response in { success, data }
    const result = data.data || data;

    if (result?.token) {
      localStorage.setItem('auth_token', result.token);
    }
    if (result?.user) {
      localStorage.setItem('user_data', JSON.stringify(result.user));
    }

    // Fetch CSRF token after successful login
    const { fetchCsrfToken } = await import('./csrf');
    fetchCsrfToken().catch((error) => {
      logger.error('[Auth] Failed to fetch CSRF token after Google login:', error);
    });

    return result;
  },

  async logout() {
    // Import clearCsrfToken here to avoid circular dependency
    const { clearCsrfToken } = await import('./csrf');

    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    clearCsrfToken(); // Clear CSRF token on logout
  },

  isAuthenticated() {
    return !!localStorage.getItem('auth_token');
  },

  async getCurrentUser(): Promise<User> {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      return JSON.parse(userData) as User;
    }
    // Fallback to API call - backend returns { success, data: { user } } or { user }
    const response = await fetchApi<{ success?: boolean; data?: { user: User }; user?: User }>('/api/auth/me');
    return response.data?.user || response.user!;
  },
};

/** Optimized route stop information */
export interface OptimizedStop {
  id: string;
  sequence: number;
  containerId: string;
  address: string;
  lat: number;
  lon: number;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  stopType: 'pickup' | 'delivery' | 'swap' | 'inspection' | 'emergency';
  estimatedDurationMinutes?: number;
  customerName?: string;
}

/** Route optimization result */
export interface OptimizationResult {
  stops: OptimizedStop[];
  totalDistanceKm: number;
  totalDurationMinutes: number;
  optimizationScore: number;
  savings: {
    distanceKm: number;
    distancePercent: number;
    durationMinutes: number;
    durationPercent: number;
  };
  stopsReorderedCount: number;
}

// Routes
export const routeApi = {
  async getTodayRoutes(): Promise<{ data: Route[] }> {
    // Backend returns { success, data: { routes } }
    const response = await fetchApi<{ success: boolean; data: { routes: Route[] } }>('/api/routes/today');
    return { data: response.data?.routes || [] };
  },

  async getRoute(id: string): Promise<{ data: Route }> {
    // Backend returns { success, data: { route } }
    const response = await fetchApi<{ success: boolean; data: { route: Route } }>(`/api/routes/${id}`);
    return { data: response.data?.route };
  },

  async updateStopStatus(routeId: string, stopId: string, status: RouteStop['status']): Promise<{ data: RouteStop }> {
    // Backend returns { success, data: { stop } }
    const response = await fetchApi<{ success: boolean; data: { stop: RouteStop } }>(`/api/routes/${routeId}/stops/${stopId}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
    return { data: response.data?.stop };
  },

  /**
   * Optimize a route by route ID
   * @param routeId - Route ID to optimize
   * @param apply - Whether to apply the optimization (default: false for preview)
   */
  async optimize(routeId: string, apply: boolean = false): Promise<{
    applied: boolean;
    original: { stops: OptimizedStop[]; stopCount: number };
    optimized: OptimizationResult;
    message: string;
  }> {
    const response = await fetchApi<{
      success: boolean;
      data: {
        applied: boolean;
        original: { stops: OptimizedStop[]; stopCount: number };
        optimized: OptimizationResult;
      };
      message?: string;
    }>(`/api/leidir/${routeId}/optimize`, {
      method: 'POST',
      body: JSON.stringify({ apply }),
    });
    return {
      applied: response.data?.applied ?? false,
      original: response.data?.original ?? { stops: [], stopCount: 0 },
      optimized: response.data?.optimized ?? {
        stops: [],
        totalDistanceKm: 0,
        totalDurationMinutes: 0,
        optimizationScore: 0,
        savings: { distanceKm: 0, distancePercent: 0, durationMinutes: 0, durationPercent: 0 },
        stopsReorderedCount: 0,
      },
      message: response.message || 'Optimization complete',
    };
  },

  /**
   * Preview optimized route without applying
   * @param routeId - Route ID to preview optimization
   */
  async optimizePreview(routeId: string): Promise<{
    original: { stops: OptimizedStop[]; stopCount: number };
    optimized: OptimizationResult;
  }> {
    const response = await fetchApi<{
      success: boolean;
      data: {
        original: { stops: OptimizedStop[]; stopCount: number };
        optimized: OptimizationResult;
        preview: boolean;
      };
    }>(`/api/leidir/${routeId}/optimization-preview`);
    return {
      original: response.data?.original ?? { stops: [], stopCount: 0 },
      optimized: response.data?.optimized ?? {
        stops: [],
        totalDistanceKm: 0,
        totalDurationMinutes: 0,
        optimizationScore: 0,
        savings: { distanceKm: 0, distancePercent: 0, durationMinutes: 0, durationPercent: 0 },
        stopsReorderedCount: 0,
      },
    };
  },

  /**
   * Optimize current driver's assigned orders on-the-fly
   * Creates a temporary optimization based on orders with locations
   * @param orders - Array of orders with location data
   * @param currentLocation - Driver's current GPS location (optional)
   */
  async optimizeOrders(
    orders: Array<{
      id: string;
      location_lat?: number;
      location_lon?: number;
      location_address?: string;
      customer_name?: string;
    }>,
    currentLocation?: { latitude: number; longitude: number }
  ): Promise<{
    optimizedOrder: string[];
    totalDistanceKm: number;
    totalDurationMinutes: number;
    savings: {
      distanceKm: number;
      durationMinutes: number;
    };
  }> {
    const response = await fetchApi<{
      success: boolean;
      data: {
        optimizedOrder: string[];
        totalDistanceKm: number;
        totalDurationMinutes: number;
        savings: {
          distanceKm: number;
          durationMinutes: number;
        };
      };
    }>('/api/okumenn/leidir/optimize', {
      method: 'POST',
      body: JSON.stringify({
        orders: orders.map(o => ({
          id: o.id,
          lat: o.location_lat,
          lon: o.location_lon,
          address: o.location_address,
          customerName: o.customer_name,
        })),
        startLocation: currentLocation ? {
          lat: currentLocation.latitude,
          lon: currentLocation.longitude,
        } : undefined,
      }),
    });
    return {
      optimizedOrder: response.data?.optimizedOrder ?? [],
      totalDistanceKm: response.data?.totalDistanceKm ?? 0,
      totalDurationMinutes: response.data?.totalDurationMinutes ?? 0,
      savings: response.data?.savings ?? { distanceKm: 0, durationMinutes: 0 },
    };
  },
};

// Orders (for driver to see assigned orders)
export const orderApi = {
  async getAssigned(): Promise<{ data: Order[] }> {
    // Use driver jobs API to get orders assigned to current driver
    // This fetches both 'active' and 'pending' status orders assigned to the driver
    const response = await fetchApi<{ success: boolean; data: { jobs: Order[]; count: number } }>('/api/okumenn/verkefni/min');
    return { data: response.data?.jobs || [] };
  },

  async completeOrder(id: string): Promise<{ data: Order }> {
    // Backend returns { success, data: { order } }
    const response = await fetchApi<{ success: boolean; data: { order: Order } }>(`/api/pantanir/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'completed', completed_date: new Date().toISOString() }),
    });
    return { data: response.data?.order };
  },
};

// Containers
export const containerApi = {
  async getById(id: string): Promise<{ container: Container }> {
    // Backend returns { success, data: { container, alerts, recentReadings } }
    const response = await fetchApi<{ success: boolean; data: { container: Container } }>(`/api/gamar/${id}`);
    return { container: response.data?.container };
  },
};

// Photos
export const photoApi = {
  async uploadPhoto(orderId: string, file: Blob, retryOnCsrf = true): Promise<any> {
    const token = localStorage.getItem('auth_token');
    const csrfToken = getCsrfToken(); // Get CSRF token for POST request

    const formData = new FormData();
    formData.append('image', file, `order-${orderId}-${Date.now()}.jpg`);
    formData.append('order_id', orderId);
    formData.append('timestamp', new Date().toISOString());

    const response = await fetch(`${API_BASE_URL}/api/innforsla/mynd`, {
      method: 'POST',
      credentials: 'include', // Send cookies
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(csrfToken && { 'X-CSRF-Token': csrfToken }), // Add CSRF token
      },
      body: formData,
    });

    if (response.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
      throw new Error('Unauthorized - redirecting to login');
    }

    // Handle 403 CSRF token validation failure
    if (response.status === 403 && retryOnCsrf) {
      const error = await response.json().catch(() => ({ error: 'Forbidden' }));

      // Check if this is a CSRF error
      if (error.error?.toLowerCase().includes('csrf')) {
        logger.warn('[API] CSRF token validation failed on upload, refreshing token and retrying...');

        // Refresh CSRF token
        await refreshCsrfToken();

        // Retry upload once with new token
        return this.uploadPhoto(orderId, file, false);
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Upload failed: ${response.status}`);
    }

    return response.json();
  },

  async getOrderPhotos(orderId: string) {
    return fetchApi<{ data: OrderPhoto[] }>(`/api/pantanir/${orderId}/myndir`);
  },
};

// =============================================================================
// Driver Jobs (Verkefni) API
// =============================================================================

/** Job/Order information for drivers */
export interface Job {
  id: string;
  customer_id: string;
  customer_name?: string;
  customer_phone?: string;
  container_id: string;
  container_type?: string;
  waste_type?: string;
  location_address?: string;
  location_lat?: number;
  location_lon?: number;
  type: 'rental' | 'pickup' | 'delivery' | 'service';
  status: 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  scheduled_date?: string;
  completed_date?: string;
  notes?: string;
  total_amount?: number;
  driver_id?: string;
  created_at: string;
  updated_at: string;
}

export const jobsApi = {
  /**
   * Get available jobs (unassigned pending/confirmed orders)
   * @param type - Optional filter by order type
   */
  async getAvailable(type?: string): Promise<{ data: Job[] }> {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    const query = params.toString() ? `?${params.toString()}` : '';

    const response = await fetchApi<{ success: boolean; data: { jobs: Job[]; count: number } }>(
      `/api/okumenn/verkefni${query}`
    );
    return { data: response.data?.jobs || [] };
  },

  /**
   * Get jobs assigned to the current driver
   * @param status - Optional filter by status
   */
  async getMyJobs(status?: string): Promise<{ data: Job[] }> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    const query = params.toString() ? `?${params.toString()}` : '';

    const response = await fetchApi<{ success: boolean; data: { jobs: Job[]; count: number } }>(
      `/api/okumenn/verkefni/min${query}`
    );
    return { data: response.data?.jobs || [] };
  },

  /**
   * Claim/take a job (assign to current driver)
   * @param jobId - Order ID to claim
   */
  async takeJob(jobId: string): Promise<{ data: Job }> {
    const response = await fetchApi<{ success: boolean; data: { order: Job; message: string } }>(
      `/api/okumenn/verkefni/${jobId}/taka`,
      { method: 'POST' }
    );
    return { data: response.data?.order };
  },

  /**
   * Complete a job
   * @param jobId - Order ID to complete
   * @param signatureDataUrl - Optional base64 PNG signature from customer
   */
  async completeJob(jobId: string, signatureDataUrl?: string): Promise<{ data: Job }> {
    const body = signatureDataUrl ? { signature: signatureDataUrl } : undefined;
    const response = await fetchApi<{ success: boolean; data: { order: Job; message: string } }>(
      `/api/okumenn/verkefni/${jobId}/ljuka`,
      {
        method: 'POST',
        ...(body && {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }),
      }
    );
    return { data: response.data?.order };
  },

  /**
   * Release a job (unassign from driver, return to available)
   * @param jobId - Order ID to release
   */
  async releaseJob(jobId: string): Promise<{ message: string }> {
    const response = await fetchApi<{ success: boolean; data: { message: string } }>(
      `/api/okumenn/verkefni/${jobId}/sleppa`,
      { method: 'POST' }
    );
    return { message: response.data?.message || 'Verkefni sleppt' };
  },

  /**
   * Cancel a job
   * @param jobId - Order ID to cancel
   */
  async cancelJob(jobId: string): Promise<{ message: string }> {
    const response = await fetchApi<{ success: boolean; data: { message: string } }>(
      `/api/okumenn/verkefni/${jobId}/haetta_vid`,
      { method: 'POST' }
    );
    return { message: response.data?.message || 'Verkefni afpantad' };
  },
};

// GPS Location Tracking
export const locationApi = {
  /**
   * Update driver GPS location
   * @param location - GPS coordinates and metadata
   */
  async updateLocation(location: {
    vehicle_id?: string;
    latitude: number;
    longitude: number;
    accuracy?: number;
    timestamp: number;
    route_id?: string;
  }) {
    return fetchApi<{
      success: boolean;
      message: string;
      location: {
        driver_id: string;
        vehicle_id: string | null;
        latitude: number;
        longitude: number;
        accuracy: number | null;
        timestamp: number;
        route_id: string | null;
      };
    }>('/api/okumenn/staðsetning', {
      method: 'POST',
      body: JSON.stringify(location),
    });
  },

  /**
   * Get latest location for a driver
   * @param driverId - Driver ID to fetch location for
   */
  async getLatestLocation(driverId: string) {
    return fetchApi<{
      success: boolean;
      location: {
        id: number;
        driver_id: string;
        vehicle_id: string | null;
        latitude: number;
        longitude: number;
        accuracy: number | null;
        timestamp: number;
        recorded_at: string;
        route_id: string | null;
        driver_name: string;
        vehicle_registration: string | null;
      };
    }>(`/api/okumenn/staðsetning/${driverId}`);
  },

  /**
   * Get location history for a driver
   * @param driverId - Driver ID to fetch history for
   * @param options - Query options (limit, since)
   */
  async getLocationHistory(
    driverId: string,
    options?: {
      limit?: number;
      since?: number;
    }
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.since) params.append('since', options.since.toString());

    const query = params.toString() ? `?${params.toString()}` : '';
    return fetchApi<{
      success: boolean;
      count: number;
      locations: Array<{
        id: number;
        driver_id: string;
        vehicle_id: string | null;
        latitude: number;
        longitude: number;
        accuracy: number | null;
        timestamp: number;
        recorded_at: string;
        route_id: string | null;
        vehicle_registration: string | null;
      }>;
    }>(`/api/okumenn/staðsetning/${driverId}/saga${query}`);
  },
};

// =============================================================================
// Expenses (Utgold) API
// =============================================================================

/** Expense category types */
export type ExpenseCategory = 'fuel' | 'parking' | 'tolls' | 'meals' | 'maintenance' | 'other';

/** Expense status types */
export type ExpenseStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';

/** Expense record from API */
export interface Expense {
  id: string;
  driver_id: string;
  description: string;
  amount_including_vat: number;
  amount_excluding_vat?: number;
  amount_vat?: number;
  vat_percentage?: number;
  expense_date: string;
  category: ExpenseCategory;
  creditor_name?: string;
  reference?: string;
  comments?: string;
  receipt_image_key?: string;
  status: ExpenseStatus;
  created_at: string;
  updated_at?: string;
}

/** Expense category with display info */
export interface ExpenseCategoryInfo {
  id: string;
  category: ExpenseCategory;
  name: string;
  description?: string;
}

/** Create expense request - matches backend /api/okumenn/utgold */
export interface CreateExpenseRequest {
  amount: number;           // Amount including VAT in ISK
  category?: ExpenseCategory;
  description?: string;     // Description (defaults to category name on backend)
  date?: string;            // Expense date (defaults to today on backend)
  creditor_name?: string;
  creditor_ssn?: string;
  receipt_image_key?: string;
  route_id?: string;
}

export const expensesApi = {
  /**
   * Get expenses for current driver
   * @param options - Filter options (date range, status, category)
   */
  async getExpenses(options?: {
    from?: string;
    to?: string;
    status?: ExpenseStatus;
    category?: ExpenseCategory;
    limit?: number;
    offset?: number;
  }): Promise<{ expenses: Expense[]; total: number }> {
    const params = new URLSearchParams();
    if (options?.from) params.append('from', options.from);
    if (options?.to) params.append('to', options.to);
    if (options?.status) params.append('status', options.status);
    if (options?.category) params.append('category', options.category);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : '';
    // Use driver-specific endpoint /api/okumenn/utgold
    const response = await fetchApi<{
      success: boolean;
      data: { expenses: Expense[]; categories?: ExpenseCategoryInfo[] };
    }>(`/api/okumenn/utgold${query}`);

    return {
      expenses: response.data?.expenses || [],
      total: response.data?.expenses?.length || 0,
    };
  },

  /**
   * Get expense by ID - not available for drivers, use getExpenses instead
   */
  async getExpense(id: string): Promise<{ expense: Expense }> {
    // Drivers don't have a single expense endpoint, fetch all and filter
    const { expenses } = await this.getExpenses({});
    const expense = expenses.find(e => e.id === id);
    return { expense: expense as Expense };
  },

  /**
   * Get expense categories - returned with getExpenses
   */
  async getCategories(): Promise<{ categories: ExpenseCategoryInfo[] }> {
    // Categories are returned with the utgold endpoint
    const response = await fetchApi<{
      success: boolean;
      data: { categories: ExpenseCategoryInfo[] };
    }>('/api/okumenn/utgold');

    return { categories: response.data?.categories || [] };
  },

  /**
   * Create new expense
   */
  async createExpense(data: CreateExpenseRequest): Promise<{ expense: Expense }> {
    // Debug: Log the data being sent
    const jsonBody = JSON.stringify(data);
    logger.info('[ExpensesApi] Creating expense:', {
      data,
      jsonBody,
      amountType: typeof data.amount,
    });

    // Use driver-specific endpoint
    const response = await fetchApi<{
      success: boolean;
      data: { expense: Expense };
    }>('/api/okumenn/utgold', {
      method: 'POST',
      body: jsonBody,
    });

    return { expense: response.data?.expense };
  },

  /**
   * Update expense (draft only) - not available for drivers
   */
  async updateExpense(_id: string, _data: Partial<CreateExpenseRequest>): Promise<{ expense: Expense }> {
    // Drivers can't update expenses after creation
    throw new Error('Ekki haegt ad breyta utgoldum eftir skraningu');
  },

  /**
   * Submit expense for approval
   */
  async submitExpense(id: string): Promise<{ expense: Expense }> {
    const response = await fetchApi<{
      success: boolean;
      data: { expense: Expense };
    }>(`/api/okumenn/utgold/${id}/submit`, {
      method: 'POST',
    });

    return { expense: response.data?.expense };
  },

  /**
   * Upload receipt image
   */
  async uploadReceipt(file: Blob, expenseId?: string): Promise<{ key: string }> {
    const token = localStorage.getItem('auth_token');
    const { getCsrfToken } = await import('./csrf');
    const csrfToken = getCsrfToken();

    const formData = new FormData();
    formData.append('receipt', file, `receipt-${Date.now()}.jpg`);
    if (expenseId) {
      formData.append('expense_id', expenseId);
    }

    // Use driver-specific upload endpoint
    const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://api.gamaleigan.is'}/api/okumenn/utgold/upload`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Failed to upload receipt');
    }

    const result = await response.json();
    return { key: result.data?.key || result.key };
  },
};

// =============================================================================
// Driver Trips (Ferdlalog) API
// =============================================================================

/** Trip status types */
export type TripStatus = 'active' | 'completed';

/** Trip record from API */
export interface Trip {
  id: string;
  driver_id: string;
  vehicle_id: string | null;
  vehicle_registration?: string | null;
  date: string;
  start_time: string | null;
  end_time: string | null;
  start_odometer_km: number | null;
  end_odometer_km: number | null;
  distance_km: number | null;
  jobs_completed: number;
  status: TripStatus;
  notes: string | null;
  created_at: string;
}

/** Start trip request */
export interface StartTripRequest {
  vehicle_id?: string;
  start_odometer_km?: number;
}

/** End trip request */
export interface EndTripRequest {
  end_odometer_km?: number;
  notes?: string;
}

export const tripsApi = {
  /**
   * Get all trips for the current driver
   * @param options - Filter options (status, limit, offset)
   */
  async getTrips(options?: {
    status?: TripStatus;
    limit?: number;
    offset?: number;
  }): Promise<{ trips: Trip[]; count: number }> {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetchApi<{
      success: boolean;
      data: { trips: Trip[]; count: number };
    }>(`/api/okumenn/ferdlalog${query}`);

    return {
      trips: response.data?.trips || [],
      count: response.data?.count || 0,
    };
  },

  /**
   * Get the active trip for the current driver
   * @returns Active trip or null if no active trip
   */
  async getActiveTrip(): Promise<{ trip: Trip | null }> {
    const response = await fetchApi<{
      success: boolean;
      data: { trip: Trip | null };
    }>('/api/okumenn/ferdlalog/virkt');

    return { trip: response.data?.trip || null };
  },

  /**
   * Start a new trip/work shift
   * @param data - Optional vehicle and odometer info
   */
  async startTrip(data?: StartTripRequest): Promise<{ trip: Trip; message: string }> {
    const response = await fetchApi<{
      success: boolean;
      data: { trip: Trip; message: string };
    }>('/api/okumenn/ferdlalog/byrja', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });

    return {
      trip: response.data?.trip,
      message: response.data?.message || 'Vinnudagur hafinn',
    };
  },

  /**
   * End the current trip/work shift
   * @param data - Optional odometer and notes
   */
  async endTrip(data?: EndTripRequest): Promise<{ trip: Trip; message: string }> {
    const response = await fetchApi<{
      success: boolean;
      data: { trip: Trip; message: string };
    }>('/api/okumenn/ferdlalog/ljuka', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });

    return {
      trip: response.data?.trip,
      message: response.data?.message || 'Vinnudegi lokið',
    };
  },
};

// =============================================================================
// Receipt OCR API
// =============================================================================

/** Receipt scan result from OCR */
export interface ReceiptScanResult {
  amount: number;
  date: string;
  vendor: string;
  category: string;
}

export const receiptOcrApi = {
  /**
   * Scan a receipt image using Gemini Vision OCR
   * @param imageBase64 - Base64-encoded image data (with or without data URL prefix)
   * @returns Extracted receipt data or null if parsing failed
   */
  async scanReceipt(imageBase64: string): Promise<{
    parsed: boolean;
    message: string;
    data: ReceiptScanResult | null;
  }> {
    const response = await fetchApi<{
      success: boolean;
      data: {
        parsed: boolean;
        message: string;
        data: ReceiptScanResult | null;
      };
    }>('/api/okumenn/utgold/scan-receipt', {
      method: 'POST',
      body: JSON.stringify({ image: imageBase64 }),
    });

    return {
      parsed: response.data?.parsed ?? false,
      message: response.data?.message ?? 'Villa',
      data: response.data?.data ?? null,
    };
  },
};
